/**
 * dashboardScope — Stream TD (Paritaet R5): Tag-Dashboards
 *
 * v1 kennt kein eigenes Tag-Dashboard, sondern nur `/dashboard?tag=<id>`
 * (app/views/tags/_tag.haml:8-13 -> dashboard_path(tag: tag.id)). v2 uebernimmt
 * genau diese URL-Form; der Scope wird als Query-Parameter `tag_id` an die
 * Dashboard-Endpunkte gereicht.
 *
 * Der Scope-Block kommt vom BE nur im Tag-Scope additiv mit — ohne `tag_id`
 * ist die Response byte-identisch zu vorher (Mobile-App-Kompatibilitaet).
 */

/** Additiver `scope`-Block der Dashboard-Endpunkte (nur im Tag-Scope gesetzt). */
export type DashboardScope = {
  kind: 'user' | 'tag'
  id: number | null
  name: string | null
  /** v1 @hide_balance — Balance UND Since-Payout entfallen im Tag-Scope. */
  hide_balance: boolean
  num_hotspots: number
  num_wallets: number
  /** Andockpunkt fuer das kuenftige Doppel-Limit-Modell (IoT vs. Mobile). */
  device_type_counts: { iot: number; mobile: number }
}

/**
 * Widgets, die das BE im Tag-Scope ausblendet (`hidden_ids`) — v1:
 * _balance.haml:1 und _payout.haml:1 pruefen beide @hide_balance.
 * Das FE fragt sie im Tag-Scope gar nicht erst an (spart einen Roundtrip)
 * und rendert sie auch dann nicht, wenn sie im gespeicherten Layout stehen.
 */
export const SCOPE_HIDDEN_WIDGET_IDS = ['balance', 'since_payout'] as const

export type ScopeHiddenWidgetId = (typeof SCOPE_HIDDEN_WIDGET_IDS)[number]

export function isScopeHiddenWidgetId(id: string): id is ScopeHiddenWidgetId {
  return (SCOPE_HIDDEN_WIDGET_IDS as readonly string[]).includes(id)
}

function num(value: unknown, fallback = 0): number {
  return typeof value === 'number' && Number.isFinite(value) ? value : fallback
}

/** Normalisiert den `scope`-Block; `null`, wenn kein (oder kein Tag-)Scope aktiv ist. */
export function normalizeScope(raw: unknown): DashboardScope | null {
  if (!raw || typeof raw !== 'object') return null
  const s = raw as Record<string, unknown>
  if (s.kind !== 'tag' && s.kind !== 'user') return null
  const counts = (s.device_type_counts ?? {}) as Record<string, unknown>
  return {
    kind: s.kind,
    id: typeof s.id === 'number' ? s.id : null,
    name: typeof s.name === 'string' ? s.name : null,
    hide_balance: s.hide_balance === true,
    num_hotspots: num(s.num_hotspots),
    num_wallets: num(s.num_wallets),
    device_type_counts: { iot: num(counts.iot), mobile: num(counts.mobile) },
  }
}

/**
 * Baut den Query-Teil fuer den Tag-Scope. Bewusst leer, wenn kein Tag gesetzt
 * ist — der parameterlose Aufruf muss unveraendert bleiben.
 */
export function tagScopeQuery(tagId?: number | null): Record<string, string> {
  return tagId ? { tag_id: String(tagId) } : {}
}

/** Query-Key-Fragment: `null` statt `undefined`, damit React-Query stabil serialisiert. */
export function tagScopeKey(tagId?: number | null): number | null {
  return tagId ?? null
}
